/**
 * ProtectedRoute — gate a route on authentication.
 *
 * Behaviour:
 *   - While the AuthContext is booting (fetching /me for an existing
 *     token), render a minimal loading shell so we never flash the
 *     auth page for a user who is actually authenticated.
 *   - If the user is not authenticated after boot, redirect to /auth
 *     with a `?redirect=<safe-path>` query so the auth page can send
 *     them back where they came from.
 *   - Otherwise render children (or an <Outlet/> when used as a layout
 *     wrapper via <Route element={<ProtectedRoute>…</ProtectedRoute>}>).
 *
 * The redirect target is sanitised: only internal paths (leading '/'
 * and no protocol / host) are honoured, so a crafted URL cannot bounce
 * the user to an external attacker page.
 */

import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';

/**
 * Only accept internal same-origin paths starting with a single '/'.
 * Reject 'https://…', '//evil.example', empty, and anything with
 * whitespace or a leading fragment.
 *
 * Intentionally colocated with ProtectedRoute — the two share the
 * same concern (safe post-login redirect target). The react-refresh
 * rule warns because mixing helper exports with a component export
 * breaks fast-refresh in dev; that trade-off is worth the locality.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function safeInternalRedirect(target) {
  if (typeof target !== 'string') return null;
  if (!target.startsWith('/')) return null;
  if (target.startsWith('//')) return null;
  if (/\s/.test(target)) return null;
  return target;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, isBooting } = useAuth();
  const location = useLocation();

  if (isBooting) {
    return (
      <div className="auth-boot" role="status" aria-live="polite">
        <span>Loading…</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search || ''}`;
    const safe = safeInternalRedirect(from);
    const target = safe && safe !== '/auth'
      ? `/auth?redirect=${encodeURIComponent(safe)}`
      : '/auth';
    return <Navigate to={target} replace />;
  }

  return children ?? <Outlet />;
}

export default ProtectedRoute;
